import { getError, toTimestamp, type WithRef } from '@principle-theorem/shared';
import { camelCase, isDate, isNull, mapKeys, mapValues } from 'lodash';
import { EMPTY, from, of, type Observable } from 'rxjs';
import { catchError, expand } from 'rxjs/operators';
import { getDatabaseConnectionName, runQuery } from './connection';
import {
  IPracticeMigration,
  ISource,
  ISourceSyncErrorData,
} from '@principle-theorem/practice-migrations/interfaces';

/**
 * Cloud SQL connection names are `project:region:instance`. Callers that need to
 * address the instance itself - status lookups, start/stop, deploy targets -
 * want only the trailing segment.
 */
export function getDatabaseInstanceName(source: ISource): string | undefined {
  return getDatabaseConnectionName(source).split(':').pop() || undefined;
}

export interface IKeysetPaginationConfig {
  cursorColumn: string;
  cursorField?: string;
}

/**
 * Out-parameter used to surface the last-seen keyset cursor id back to the
 * caller once a `runSourceQuery$` keyset run completes. The caller passes in
 * an empty object (`{}`); the helper writes `value` after each page so the
 * caller can read the final cursor position after the observable completes.
 */
export interface IKeysetCursorRef {
  value?: number;
}

/**
 * DEPRECATED use runSourceQuery$ instead
 * @param queryFn
 * @param migration
 * @param limit
 * @param initialOffset
 * @param endOffset
 * @returns
 */
export function runSourceQueryWithFunction$<T>(
  queryFn: (
    offest: number,
    limit: number,
    migration: WithRef<IPracticeMigration>
  ) => Promise<T[]>,
  migration: WithRef<IPracticeMigration>,
  limit: number = 100000,
  initialOffset: number = 0,
  endOffset?: number
): Observable<T[] | ISourceSyncErrorData> {
  let offset = initialOffset;
  return from(queryFn(offset, limit, migration)).pipe(
    expand((data) => {
      offset += limit;
      const isPastOffset = endOffset ? offset > endOffset : false;
      if (isPastOffset || !data.length) {
        return EMPTY;
      }
      return queryFn(offset, limit, migration);
    }),
    catchError((error) =>
      of({
        resumeData: { offset: offset },
        errorMessage: getError(error),
      })
    )
  );
}

export const OFFSET_PLACEHOLDER = 'OFFSET_PLACEHOLDER';

/**
 * Placeholder used in keyset-paginated queries. The helper replaces it with
 * `WHERE <cursorColumn> > <lastId> ORDER BY <cursorColumn> ASC LIMIT <n>` on
 * each page. Must appear where the trailing ORDER BY / LIMIT clause goes.
 *
 * Queries using this placeholder must NOT include `OFFSET_PLACEHOLDER`.
 */
export const KEYSET_PLACEHOLDER = 'KEYSET_PLACEHOLDER';

/**
 * Exclusive lower bound for the first page. Sits below the smallest id any
 * cursor column can hold, whether that column is zero- or one-based.
 */
const KEYSET_START_CURSOR = -1;

export function runSourceQuery$<QueryResult, TransformResult = QueryResult>(
  sourceMigration: WithRef<IPracticeMigration>,
  queryStrings: string | string[],
  transformRecords: (records: QueryResult[]) => TransformResult[] = (records) =>
    records as unknown as TransformResult[],
  limit: number = 100000,
  initialOffset: number = 0,
  endOffset?: number,
  _skipMigrated?: boolean,
  keysetConfig?: IKeysetPaginationConfig,
  keysetCursorRef?: IKeysetCursorRef
): Observable<TransformResult[] | ISourceSyncErrorData> {
  if (limit === 0) {
    limit = 999999999;
  }

  if (keysetConfig) {
    return runKeysetQuery$(
      sourceMigration,
      queryStrings,
      transformRecords,
      limit,
      initialOffset,
      keysetConfig,
      keysetCursorRef
    );
  }

  let offset = initialOffset;

  if (endOffset && endOffset - initialOffset < limit) {
    limit = endOffset - initialOffset;
  }

  // const existsQuery = skipMigrated ? `NOT EXISTS(SELECT 1 FROM source_entity_records WHERE ${whereCondition})` : '';

  const queryFn = async (
    queryOffset: number,
    queryLimit: number,
    migration: WithRef<IPracticeMigration>
  ): Promise<TransformResult[]> => {
    const offsetStatement = `LIMIT ${queryLimit} OFFSET ${queryOffset}`;

    const queries = Array.isArray(queryStrings) ? queryStrings : [queryStrings];
    const formattedQueries = queries.map((query) => {
      if (query.includes(OFFSET_PLACEHOLDER)) {
        return query.replace(OFFSET_PLACEHOLDER, offsetStatement);
      }
      return `${query} ${offsetStatement};`;
    });

    const response = await runQuery<QueryResult>(migration, formattedQueries);
    return transformRecords(response.rows);
  };

  return from(queryFn(offset, limit, sourceMigration)).pipe(
    expand((data) => {
      if (endOffset && endOffset - offset < limit) {
        limit = endOffset - offset;
      }
      offset += limit;
      const isPastOffset = endOffset ? offset >= endOffset : false;
      if (isPastOffset || !data.length) {
        return EMPTY;
      }
      return queryFn(offset, limit, sourceMigration);
    }),
    catchError((error) =>
      of({
        resumeData: { offset: offset },
        errorMessage: getError(error),
      })
    )
  );
}

/**
 * Seek (keyset) pagination path for `runSourceQuery$`.
 *
 * `initialOffset` is re-used as the last-seen cursor id. Each page replaces
 * `${KEYSET_PLACEHOLDER}` with
 * `AND <col> > <lastId> ORDER BY <col> ASC LIMIT <n>` and reads the cursor id
 * from the last row (all keyset entities select their cursor column as `id`).
 *
 * Cursor columns are not unique — Exact's `_record_id` is a reused counter, so
 * the strict `>` would skip whatever a page boundary cut off mid-run. A filled
 * page drops its trailing run so the next page reads that run whole. A run
 * that fills a page on its own is re-read with a larger limit.
 *
 * The outer window ends once `limit` rows have been consumed; the Cloud
 * Tasks loop then re-queues with the new cursor via `keysetCursorRef`.
 */
function runKeysetQuery$<QueryResult, TransformResult>(
  sourceMigration: WithRef<IPracticeMigration>,
  queryStrings: string | string[],
  transformRecords: (records: QueryResult[]) => TransformResult[],
  limit: number,
  initialOffset: number,
  keysetConfig: IKeysetPaginationConfig,
  keysetCursorRef?: IKeysetCursorRef
): Observable<TransformResult[] | ISourceSyncErrorData> {
  const { cursorColumn } = keysetConfig;
  const cursorField = keysetConfig.cursorField ?? 'id';
  let lastId = initialOffset === 0 ? KEYSET_START_CURSOR : initialOffset;
  let rowsConsumedInWindow = 0;

  function readCursor(row: TransformResult): number {
    return parseInt(String((row as Record<string, unknown>)[cursorField]), 10);
  }

  function dropTrailingCursorRun(rows: TransformResult[]): TransformResult[] {
    const tailCursor = readCursor(rows[rows.length - 1]);
    return rows.filter((row) => readCursor(row) !== tailCursor);
  }

  const queryFn = async (
    currentLastId: number,
    queryLimit: number,
    migration: WithRef<IPracticeMigration>
  ): Promise<TransformResult[]> => {
    const keysetClause = `AND ${cursorColumn} > ${currentLastId}\nORDER BY ${cursorColumn} ASC\nLIMIT ${queryLimit}`;
    const queries = Array.isArray(queryStrings) ? queryStrings : [queryStrings];
    const formattedQueries = queries.map((query) =>
      query.replace(KEYSET_PLACEHOLDER, keysetClause)
    );
    const response = await runQuery<QueryResult>(migration, formattedQueries);
    const rows = transformRecords(response.rows);
    const filledPage = response.rows.length >= queryLimit;
    if (!filledPage || !rows.length) {
      return rows;
    }
    const completeRuns = dropTrailingCursorRun(rows);
    if (completeRuns.length) {
      return completeRuns;
    }
    return queryFn(currentLastId, queryLimit * 2, migration);
  };

  return from(queryFn(lastId, limit, sourceMigration)).pipe(
    expand((data) => {
      if (!data.length) {
        return EMPTY;
      }
      lastId = readCursor(data[data.length - 1]);

      if (Number.isNaN(lastId)) {
        throw new Error(
          `Keyset cursor is NaN for cursorField "${cursorField}" ` +
            `(cursorColumn "${keysetConfig.cursorColumn}"). ` +
            `Ensure the query selects the cursor column as "${cursorField}" ` +
            `and that it resolves to a numeric string on every row.`
        );
      }

      rowsConsumedInWindow += data.length;

      if (keysetCursorRef) {
        keysetCursorRef.value = lastId;
      }

      if (rowsConsumedInWindow >= limit) {
        return EMPTY;
      }
      return queryFn(lastId, limit, sourceMigration);
    }),
    catchError((error) =>
      of({
        resumeData: { offset: lastId },
        errorMessage: getError(error),
      })
    )
  );
}

export function convertKeysToCamelCaseFn<T extends object>(): (
  data: T[]
) => T[] {
  return (results: T[]): T[] =>
    results.map((result) =>
      mapKeys(result, (_, key) => camelCase(key.toString()))
    ) as unknown as T[];
}

export function convertNullToUndefinedFn<T extends object>(): (
  data: T[]
) => T[] {
  return (results: T[]): T[] =>
    results.map((result) =>
      mapValues(result, (value) => (isNull(value) ? undefined : value))
    ) as unknown as T[];
}

export function convertDateToTimestampFn<T extends object>(): (
  data: T[]
) => T[] {
  return (results: T[]): T[] =>
    results.map((result) =>
      mapValues(result, (value) => (isDate(value) ? toTimestamp(value) : value))
    ) as unknown as T[];
}

export function convertValueFn<T extends object, R>(
  translateFn: (value: unknown) => R,
  ...properties: (keyof T)[]
): (data: T[]) => T[] {
  return (results: T[]): T[] =>
    results.map((result) =>
      mapValues(result, (value, key) =>
        properties.includes(key as keyof T) ? translateFn(value) : value
      )
    ) as unknown as T[];
}
